/**
 * useBackButton - Master back button handler for PWA
 * 
 * Manages browser history state so the native back button (iOS/Android)
 * closes modals and navigates between tabs instead of leaving the app.
 * 
 * Strategy:
 * - Push a history entry when a modal opens or tab changes from 'home'
 * - On popstate (back button), close the topmost modal or return to 'home'
 * - Prevent the back button from ever navigating away from the app
 */

import { useEffect, useRef, useCallback } from 'react';

const useBackButton = ({
  currentView,
  setCurrentView,
  // Modal states - ordered by priority (most likely to be topmost)
  modals
}) => {
  const isHandlingPopState = useRef(false);
  const lastPushedState = useRef(null);

  // Get list of currently open modals
  const getOpenModals = useCallback(() => {
    return modals.filter(m => m.isOpen);
  }, [modals]);

  // Push history state when navigating away from home or opening a modal
  useEffect(() => {
    const openModals = getOpenModals();
    const hasOpenModal = openModals.length > 0;
    const isNotHome = currentView !== 'home' && currentView !== 'auth';

    // Build a state key representing current navigation depth
    const stateKey = hasOpenModal
      ? `modal:${openModals[openModals.length - 1]?.name}`
      : isNotHome
        ? `view:${currentView}`
        : 'home';

    // Only push if state actually changed and we're not already handling a popstate
    if (!isHandlingPopState.current && stateKey !== 'home' && stateKey !== lastPushedState.current) {
      window.history.pushState({ sp: stateKey }, '', window.location.pathname);
      lastPushedState.current = stateKey;
    }
  }, [currentView, getOpenModals]);

  // Listen for the back button (popstate)
  useEffect(() => {
    const handlePopState = (event) => {
      isHandlingPopState.current = true;

      const openModals = getOpenModals();

      if (openModals.length > 0) {
        // Close the topmost (last) open modal
        const topModal = openModals[openModals.length - 1];
        topModal.close();
      } else if (currentView !== 'home' && currentView !== 'auth') {
        // Navigate back to home
        setCurrentView('home');
      } else {
        // Already at home with no modals — push state back to prevent leaving
        window.history.pushState({ sp: 'home' }, '', window.location.pathname);
      }

      // Update tracking
      const remainingModals = getOpenModals().filter(m => m.isOpen);
      if (remainingModals.length === 0 && currentView === 'home') {
        lastPushedState.current = 'home';
      }

      // Reset flag after a tick
      setTimeout(() => {
        isHandlingPopState.current = false;
      }, 100);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentView, setCurrentView, getOpenModals]);

  // On mount, ensure we have a base history entry
  useEffect(() => {
    // Replace the current entry with our base state
    window.history.replaceState({ sp: 'home' }, '', window.location.pathname);
  }, []);
};

export default useBackButton;
